import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { ImportFlow } from "../components/ImportFlow";
import { ImportCategoryBanner } from "../components/ImportCategoryBanner";
// Iter53 P36 — self-contained live-progress widget
import { ImportProgressWidget } from "../components/ImportProgressWidget";
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Clock,
  RefreshCw,
  FileText,
  Database,
  Square,
  Ban,
  FlaskConical,
  Sparkles,
  Activity,
  ShieldCheck,
  ListChecks,
  Download,
  StickyNote
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ImportPage = () => {
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState("easy");
  const [uploading, setUploading] = useState(false);
  const [importNotes, setImportNotes] = useState({ easy: "", salesforce: "", crm: "" });
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [showResultDialog, setShowResultDialog] = useState(false);
  // Iter51 Etapa 20 — error details viewer (clickable rows_errors → JSON drawer)
  const [errorDetailsBatch, setErrorDetailsBatch] = useState(null);
  const [errorDetailsFull, setErrorDetailsFull] = useState(null);
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);

  const [sfAllowCreate, setSfAllowCreate] = useState(false);
  // Iter51 E27 — dry-run simulation state
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  // Iter51 E30 — pre-flight header/quality check state
  const [preflighting, setPreflighting] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);
  // Iter51 E33 Faza 3 — editable column overrides + saved profiles
  const [columnOverrides, setColumnOverrides] = useState({}); // {field_label: column_name | null}
  const [mappingProfiles, setMappingProfiles] = useState([]); // list of all profiles
  const [selectedProfileId, setSelectedProfileId] = useState({}); // {easy:id, salesforce:id, crm:id}
  const [savingProfile, setSavingProfile] = useState(false);
  // Iter51 E26 — cancel confirmation dialog state
  const [cancelTarget, setCancelTarget] = useState(null);  // batch object pending cancel
  const [cancelInFlight, setCancelInFlight] = useState(false);
  // Iter51 E26 — filter pentru tabelul Istoric: 'all' | 'processing' | 'completed' | 'failed'
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  // Iter51 E27b — filename search + extended limit
  const [historyFilenameQuery, setHistoryFilenameQuery] = useState("");
  const [historyLimit, setHistoryLimit] = useState(50);
  // Iter51 E25b — ref to track active polling so re-opening the dialog from
  // Istoric doesn't spawn a 2nd parallel poll for the same batch.
  const activePollRef = useRef(null);

  // Iter52 P9 — Click-to-map modal state (admin maps unknown category → kind)
  const [mapModal, setMapModal] = useState(null); // { value, source, originator } | null
  const [mapKind, setMapKind] = useState("EE");
  const [mapMatchType, setMapMatchType] = useState("exact");
  const [savingRule, setSavingRule] = useState(false);

  // Iter52 P10 — Batch changes dialog (Activity-style log for a specific ImportBatch)
  const [batchChangesTarget, setBatchChangesTarget] = useState(null);
  const [batchChangesData, setBatchChangesData] = useState(null);
  const [batchChangesLoading, setBatchChangesLoading] = useState(false);

  // Iter63c — deep-link din Activitate (/import?batch=<id>): evidențiază + derulează la lot
  const highlightBatchId = new URLSearchParams(window.location.search).get("batch");
  useEffect(() => {
    if (!highlightBatchId || !batches.length) return;
    document.getElementById(`batch-row-${highlightBatchId}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightBatchId, batches.length]);

  const fetchBatches = useCallback(async (statusFilter, filenameQuery, limitOverride) => {
    try {
      const effStatus = statusFilter ?? historyStatusFilter;
      const effFilename = filenameQuery ?? historyFilenameQuery;
      const effLimit = limitOverride ?? historyLimit;
      const params = new URLSearchParams({ limit: String(effLimit) });
      if (effStatus && effStatus !== "all") params.set("status", effStatus);
      if (effFilename && effFilename.trim().length >= 2) {
        params.set("filename_contains", effFilename.trim());
      }
      const response = await axios.get(`${API_URL}/import/batches?${params.toString()}`);
      setBatches(response.data);
    } catch (error) {
      console.error("Failed to fetch import batches");
    } finally {
      setLoadingBatches(false);
    }
  }, [historyStatusFilter, historyFilenameQuery, historyLimit]);

  // Iter51 E27 — handler that runs a dry-run simulation (no DB writes).
  const handleSfSimulate = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx") && !file.name.toLowerCase().endsWith(".xls")) {
      toast.error("Format invalid — selectează un fișier .xlsx");
      return;
    }
    setSimulating(true);
    setSimulationResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("allow_create", sfAllowCreate ? "true" : "false");
      const r = await axios.post(`${API_URL}/import/salesforce/simulate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Iter62 — 5 min: fișierele SF mari (6-10MB) depășeau 120s la parsare
        timeout: 5 * 60 * 1000,
      });
      setSimulationResult(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Eroare la simulare";
      toast.error(`Simulare eșuată: ${msg}`);
    } finally {
      setSimulating(false);
    }
  }, [sfAllowCreate]);

  // Iter51 E30 — pre-flight: validates SF file headers + per-field empty stats
  // before launching a multi-hour import. Read-only, no DB writes.
  // Iter51 E33 Faza 2 — extended to support Easy + CRM Legacy via `source` param.
  const handleSfPreflight = useCallback(async (file, source = "salesforce") => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx") && !file.name.toLowerCase().endsWith(".xls")) {
      toast.error("Format invalid — selectează un fișier .xlsx sau .xls");
      return;
    }
    setPreflighting(true);
    setPreflightResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_URL}/import/${source}/preflight`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Iter62 — 5 min: fișierele SF mari (6-10MB) depășeau 120s la parsare
        timeout: 5 * 60 * 1000,
      });
      setPreflightResult({ ...r.data, _source: source });
      // Iter51 E33 Faza 3 — reset overrides to current selectedProfile mappings
      // (so the dialog starts pre-filled from a saved profile if applicable)
      const profId = selectedProfileId[source];
      const prof = profId ? mappingProfiles.find((p) => p.id === profId) : null;
      setColumnOverrides(prof ? { ...(prof.mappings || {}) } : {});
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Eroare la verificare";
      toast.error(`Pre-flight eșuat: ${msg}`);
    } finally {
      setPreflighting(false);
    }
  }, [selectedProfileId, mappingProfiles]);

  // Iter51 E33 Faza 3 — fetch saved profiles on mount (once)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/import/mapping-profiles`);
        setMappingProfiles(r.data || []);
      } catch (e) {
        // Not fatal — admin without backoffice role will get 403 here; ignore silently
        if (e.response?.status !== 403) {
          console.warn("Could not load mapping profiles:", e.message);
        }
      }
    })();
  }, []);

  // Iter51 E33 Faza 3 — save current overrides as a new profile
  const handleSaveProfile = useCallback(async () => {
    if (!preflightResult) return;
    const name = window.prompt(
      `Salvează acest profil de mapping pentru sursa „${preflightResult._source}":\n\n` +
      "Introdu un nume descriptiv:",
      `Profil ${preflightResult._source} ${new Date().toLocaleDateString("ro-RO")}`,
    );
    if (!name || !name.trim()) return;
    setSavingProfile(true);
    try {
      const r = await axios.post(`${API_URL}/import/mapping-profiles`, {
        source: preflightResult._source,
        name: name.trim(),
        mappings: columnOverrides,
        is_default: false,
      });
      setMappingProfiles((prev) => [r.data, ...prev]);
      setSelectedProfileId((prev) => ({ ...prev, [preflightResult._source]: r.data.id }));
      toast.success(`Profil „${name.trim()}” salvat.`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Nu am putut salva profilul: ${msg}`);
    } finally {
      setSavingProfile(false);
    }
  }, [preflightResult, columnOverrides]);

  // Iter52 P9 — Click-to-map: when admin clicks an unknown category in the banner,
  // open a modal to pick a kind. On save, POST to product-classification-rules,
  // then re-run the originating preflight/simulate so the banner reflects the new
  // classification without requiring a manual re-upload.
  // Iter52 P14e — Backoffice has full CRUD on classification rules (was admin-only).
  const isAdmin = (user?.role === "admin" || user?.role === "backoffice");
  const openMapModal = useCallback((value, originator /* 'preflight' | 'simulate' */) => {
    if (!isAdmin) {
      toast.error("Doar admin/backoffice pot adăuga reguli de clasificare");
      return;
    }
    const src = originator === "simulate" ? "salesforce" : (preflightResult?._source || "salesforce");
    setMapKind("EE");
    setMapMatchType("exact");
    setMapModal({ value, source: src, originator });
  }, [isAdmin, preflightResult]);

  const handleSaveRule = useCallback(async () => {
    if (!mapModal) return;
    setSavingRule(true);
    try {
      await axios.post(`${API_URL}/admin/product-classification-rules`, {
        pattern: mapModal.value,
        match_type: mapMatchType,
        kind: mapKind,
        source: mapModal.source,
        active: true,
      });
      toast.success(`Regulă salvată: „${mapModal.value}" → ${mapKind}`);
      setMapModal(null);
      // Note: we don't have the original File handle here; user must re-run
      // the pre-flight / simulate flow to see the new classification reflected
      // in the banner. This is consistent with the rest of the import UX.
      toast(`Repornește ${mapModal.originator === "simulate" ? "simularea" : "pre-flight"} ca să vezi efectul.`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Nu am putut salva regula: ${msg}`);
    } finally {
      setSavingRule(false);
    }
  }, [mapModal, mapMatchType, mapKind, preflightResult]);

  // Iter52 P10 — Fetch batch changes when dialog opens
  useEffect(() => {
    if (!batchChangesTarget) {
      setBatchChangesData(null);
      return;
    }
    let cancelled = false;
    setBatchChangesLoading(true);
    setBatchChangesData(null);
    axios
      .get(`${API_URL}/import/batches/${batchChangesTarget.id}/changes?limit=2000`)
      .then((r) => {
        if (!cancelled) setBatchChangesData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Eroare la încărcarea modificărilor");
        }
      })
      .finally(() => {
        if (!cancelled) setBatchChangesLoading(false);
      });
    return () => { cancelled = true; };
  }, [batchChangesTarget]);

  // Iter51 E26 — handler that triggers cancellation of a running batch.
  const handleCancelImport = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelInFlight(true);
    try {
      const r = await axios.post(`${API_URL}/import/batches/${cancelTarget.id}/cancel`);
      toast.success(r.data?.message || "Cererea de oprire a fost trimisă.", { duration: 6000 });
      // Refresh batches so the user can see status flip to 'cancelled' on next poll
      setTimeout(() => fetchBatches(), 1500);
    } catch (e) {
      const msg = e.response?.data?.detail || "Nu am putut opri importul.";
      toast.error(msg);
    } finally {
      setCancelInFlight(false);
      setCancelTarget(null);
    }
  }, [cancelTarget, fetchBatches]);

  // Iter51 E27b — admin force-mark a stale batch as failed (cleanup helper).
  const handleMarkBatchFailed = useCallback(async (batch) => {
    if (!window.confirm(
      `Marchezi acest batch ca eșuat?\n\n${batch.filename}\n\n` +
      `Acțiunea e ireversibilă. Datele deja commit-uite (dacă există) sunt păstrate. ` +
      `Re-uploadează fișierul ca să termini importul.`
    )) return;
    try {
      const r = await axios.post(`${API_URL}/import/batches/${batch.id}/mark-failed`);
      toast.success(r.data?.message || "Batch marcat ca eșuat.");
      fetchBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la marcare.");
    }
  }, [fetchBatches]);

  // Iter62 — reluare import SF întrerupt (worker mort după deploy/restart).
  // Încearcă întâi fără fișier (serverul poate avea încă fișierul pe disc);
  // dacă serverul cere re-upload, deschide file picker-ul.
  const resumeFileRef = useRef(null);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [resuming, setResuming] = useState(false);

  const attemptResume = useCallback(async (batch, file) => {
    setResuming(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      const r = await axios.post(`${API_URL}/import/batches/${batch.id}/resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
      });
      toast.success(r.data?.message || "Import reluat.", { duration: 8000 });
      fetchBatches();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Eroare la reluare";
      if (e.response?.status === 400 && !file && /Reîncarcă|nu mai există/i.test(msg)) {
        toast.info("Fișierul sursă nu mai e pe server — selectează exact același fișier pentru reluare.", { duration: 8000 });
        setResumeTarget(batch);
        resumeFileRef.current?.click();
      } else {
        toast.error(`Reluare eșuată: ${msg}`);
      }
    } finally {
      setResuming(false);
    }
  }, [fetchBatches]);

  // Iter65c — previzualizare explicită a planului (nu pornește importul)
  const formatPlan = (batch, p) => {
    const r = p.ranges || {};
    const rng = (x) => (x ? `${x[0]}–${x[1]}` : "—");
    return (
      `Plan reluare „${batch.filename}" (previzualizare, nimic nu pornește)\n\n` +
      `Total rânduri: ${p.rows_total}\n` +
      `Contoare istorice (procesate, inclusiv nesalvate): ${p.rows_processed_counters}\n` +
      `Confirmat salvat în DB: ${p.committed_rows} (metodă: ${p.method}) — dovezi: vânzări create în lot ${p.evidence?.sales_created_in_batch}, contracte atinse ${p.evidence?.field_history_contracts_touched}, rezultate rând salvate ${p.evidence?.row_outcomes_saved}\n\n` +
      `Rândul de reluare: ${p.skip_rows + 1}\n` +
      `Intervale: sărite (comise) ${rng(r.skipped_committed)} · reprocesate (încercate, nesalvate) ${rng(r.reprocess_unsaved)} · neîncercate ${rng(r.never_attempted)}\n` +
      `De procesat acum: ${p.rows_to_process} rânduri\n` +
      `Contoare de bază la reluare: create ${p.base_counts.created}, actualizate ${p.base_counts.updated}, ignorate ${p.base_counts.skipped}, erori ${p.base_counts.errors}\n` +
      `${p.estimate_note}\n` +
      `Fișier sursă pe server: ${p.source_file_available ? "da" : "NU"} · raport parțial: ${p.has_partial_report ? "da" : "nu"}`
    );
  };
  const showResumePlan = useCallback(async (batch) => {
    try {
      const p = (await axios.get(`${API_URL}/import/batches/${batch.id}/resume-plan`)).data;
      window.alert(formatPlan(batch, p));
    } catch { toast.error("Nu am putut calcula planul de reluare"); }
  }, []);

  // Iter64b — dry-run înainte de reluare: de la ce rând, ce se reprocesează, câte rămân
  const confirmResumeWithPlan = useCallback(async (batch) => {
    try {
      const p = (await axios.get(`${API_URL}/import/batches/${batch.id}/resume-plan`)).data;
      const msg = formatPlan(batch, p) + "\n\nCONTINUI cu reluarea?";
      if (!window.confirm(msg)) return;
    } catch (e) {
      if (!window.confirm("Nu am putut calcula planul de reluare. Continui oricum?")) return;
    }
    attemptResume(batch, null);
  }, [attemptResume]);

  const handleResumeFileSelected = useCallback((e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const target = resumeTarget;
    setResumeTarget(null);
    if (f && target) attemptResume(target, f);
  }, [resumeTarget, attemptResume]);

  const renderResumeButton = (batch) =>
    batch.can_resume && hasRole(["admin", "backoffice"]) ? (
      <button
        type="button"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal-50 text-teal-700 border border-teal-300 hover:bg-teal-100 text-xs font-medium disabled:opacity-50"
        onClick={() => confirmResumeWithPlan(batch)}
        disabled={resuming}
        data-testid={`resume-import-${batch.id}`}
        title="Reia importul de la ultimul punct salvat (nu de la zero). Dacă serverul nu mai are fișierul, ți se va cere să-l reîncarci."
      >
        <RefreshCw className="h-3 w-3" />
        Reia
      </button>
    ) : null;

  // Iter52 P4 — Download per-row audit report (Excel with Status / Motiv / ID columns)
  const handleDownloadReport = useCallback(async (batch) => {
    try {
      const r = await axios.get(`${API_URL}/import/batches/${batch.id}/report.xlsx`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const cleanName = (batch.filename || "fisier").replace(/\.[^.]+$/, "");
      a.download = `raport_${batch.source || "import"}_${cleanName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nu s-a putut descărca raportul.");
    }
  }, []);

  // Iter51 E25b — reusable poll loop: opens/updates the dialog with live counters
  // until status leaves "processing". Returns a promise that resolves on terminal status.
  const pollBatchUntilDone = useCallback(async (batchId, opts = {}) => {
    const {
      isSlowSource = true,
      startedAt = Date.now(),
      showToastOnFinish = true,
    } = opts;
    if (activePollRef.current === batchId) return;  // already polling this batch
    activePollRef.current = batchId;
    const maxPolls = isSlowSource ? (4 * 60 * 60) / 3 : 30;
    const intervalMs = isSlowSource ? 3000 : 2000;
    try {
      for (let i = 0; i < maxPolls; i++) {
        // First iteration: fetch immediately to update dialog without waiting
        if (i > 0) await new Promise((r) => setTimeout(r, intervalMs));
        try {
          // Iter53 P36 — use lightweight live-progress endpoint for polling (350B payload
          // vs multi-KB /import/batches/{id}). Full payload is fetched only on terminal.
          const b = await axios.get(`${API_URL}/import/batches/${batchId}/live-progress`);
          const st = b.data?.status;
          setImportResult((prev) => ({
            ...(prev || {}),
            batch_id: batchId,
            status: st,
            filename: b.data?.filename ?? prev?.filename,
            rows_total: b.data?.rows_total ?? prev?.rows_total,
            rows_created: b.data?.rows_created ?? prev?.rows_created,
            rows_updated: b.data?.rows_updated ?? prev?.rows_updated,
            rows_skipped: b.data?.rows_skipped ?? prev?.rows_skipped,
            rows_errors: b.data?.rows_errors ?? prev?.rows_errors,
            cancellation_requested: b.data?.cancellation_requested ?? prev?.cancellation_requested,
            _polling_started_at: prev?._polling_started_at || startedAt,
          }));
          if (st === "completed" || st === "partial" || st === "failed" || st === "cancelled" || st === "interrupted") {
            // Fetch full payload once on terminal to grab client_summary for the summary view.
            let fullData = b.data;
            try {
              const full = await axios.get(`${API_URL}/import/batches/${batchId}`);
              fullData = full.data;
              setImportResult((prev) => ({
                ...(prev || {}),
                client_summary: full.data?.client_summary || prev?.client_summary || null,
              }));
            } catch {
              // ignore — live-progress payload is enough for headline counters
            }
            // Iter52 P5 — finalize progress bar so UI stops showing "în curs"
            setProgress(100);
            fetchBatches();
            if (showToastOnFinish) {
              if (st === "completed" || st === "partial") {
                const c = fullData?.rows_created || 0;
                const u = fullData?.rows_updated || 0;
                if (c > 0 || u > 0) {
                  toast.success(`Import finalizat: ${c} create, ${u} actualizate`);
                }
              } else if (st === "failed") {
                toast.error("Importul a eșuat. Verifică detaliile erorii.");
              } else if (st === "interrupted") {
                toast.warning("Importul a fost întrerupt (worker oprit). Poți relua de la ultimul punct salvat din Istoric.");
              }
            }
            return fullData;
          }
        } catch {
          // ignore poll errors; user can refresh manually
        }
      }
    } finally {
      activePollRef.current = null;
    }
  }, [fetchBatches]);

  // Iter51 E25b — re-open progress dialog from Istoric for a processing batch.
  const handleViewProgress = useCallback(async (batch) => {
    try {
      const r = await axios.get(`${API_URL}/import/batches/${batch.id}`);
      // Estimate elapsed from imported_at (no exact "polling start" stored on backend).
      const importedAtMs = batch.imported_at ? Date.parse(batch.imported_at) : Date.now();
      setImportResult({
        batch_id: batch.id,
        status: r.data?.status,
        filename: r.data?.filename,
        rows_total: r.data?.rows_total,
        rows_created: r.data?.rows_created,
        rows_updated: r.data?.rows_updated,
        rows_skipped: r.data?.rows_skipped,
        rows_errors: r.data?.rows_errors,
        client_summary: r.data?.client_summary || null,
        _polling_started_at: importedAtMs,
      });
      setShowResultDialog(true);
      if (r.data?.status === "processing") {
        // Spawn polling in background; don't await (UI is responsive immediately)
        pollBatchUntilDone(batch.id, {
          isSlowSource: batch.source === "salesforce",
          startedAt: importedAtMs,
          showToastOnFinish: true,
        });
      }
    } catch (e) {
      toast.error("Nu am putut deschide detaliile batch-ului.");
    }
  }, [pollBatchUntilDone]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // Iter51 Etapa 20 — fetch full batch with error_details when user clicks rows_errors
  useEffect(() => {
    if (!errorDetailsBatch?.id) { setErrorDetailsFull(null); return; }
    let cancel = false;
    setLoadingErrorDetails(true);
    setErrorDetailsFull(null);
    axios.get(`${API_URL}/import/batches/${errorDetailsBatch.id}`)
      .then((r) => { if (!cancel) setErrorDetailsFull(r.data); })
      .catch(() => { if (!cancel) setErrorDetailsFull({ error: "Eroare la încărcarea detaliilor" }); })
      .finally(() => { if (!cancel) setLoadingErrorDetails(false); });
    return () => { cancel = true; };
  }, [errorDetailsBatch]);

  const handleFileUpload = async (e, source) => {
    // Iter51 E33 — accept either a synthetic event OR a File directly.
    // ImportFlow component calls us with a File; legacy callsites still pass
    // the change event (e.target.files[0]).
    const isFile = e && typeof e === "object" && "name" in e && "size" in e && !("target" in e);
    const file = isFile ? e : e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.xlsx', '.xls'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExt)) {
      toast.error("Fișier invalid. Acceptăm doar .xlsx sau .xls");
      return;
    }

    setUploading(true);
    setProgress(10);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", file);
    // Iter51 Etapa 6 — SF importer flag: allow creating Sale+Contract+Client for unknown PODs
    if (source === "salesforce" && sfAllowCreate) {
      formData.append("allow_create", "true");
    }
    // Iter51 E33 Faza 3 — attach column overrides and/or profile id if set
    if (Object.keys(columnOverrides).length > 0) {
      formData.append("column_overrides", JSON.stringify(columnOverrides));
    }
    const pid = selectedProfileId[source];
    if (pid) {
      formData.append("mapping_profile_id", pid);
    }
    // Notă opțională despre ce reprezintă importul (apare sub fiecare import în istoric)
    const note = (importNotes[source] || "").trim();
    if (note) {
      formData.append("note", note);
    }

    try {
      setProgress(30);
      const response = await axios.post(`${API_URL}/import/${source}`, formData, {
        // Iter51 Etapa 22 — explicit 5-minute timeout for large file uploads.
        // K8s ingress sometimes drops connections on slow uploads; axios default
        // is no timeout which lets the browser timeout (~120s on some).
        timeout: 5 * 60 * 1000,
        maxContentLength: 200 * 1024 * 1024,
        maxBodyLength: 200 * 1024 * 1024,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total) + 30;
          setProgress(percentCompleted);
        }
      });

      // Iter52 P5 — fix "UI rămâne blocat" / arată "Finalizat 0/0" pe upload-uri mari.
      // Endpoint-ul returnează IMEDIAT cu zerouri (background-ul tocmai a pornit).
      // Nu mai afișăm "finalizat fără modificări" și nu setăm progress=100% — lăsăm
      // polling-ul să escaladeze valorile reale pe măsură ce batch-ul procesează.
      const isAsyncBatch = !!response.data.batch_id && (response.data.status === "processing" || response.data.rows_total === 0);
      if (isAsyncBatch) {
        setProgress(70);  // upload done, processing starts now
        setImportResult({
          ...response.data,
          status: "processing",
          _polling_started_at: Date.now(),
        });
        setShowResultDialog(true);
        toast.info("Fișier urcat. Importul rulează în background — progresul se actualizează automat.", { duration: 4000 });
      } else {
        // Synchronous import (small file or legacy path that finished before responding)
        setProgress(100);
        setImportResult(response.data);
        setShowResultDialog(true);
        if (response.data.rows_created > 0 || response.data.rows_updated > 0) {
          toast.success(`Import reușit: ${response.data.rows_created} create, ${response.data.rows_updated} actualizate`);
        } else {
          toast.warning("Import finalizat fără modificări");
        }
      }
      
      fetchBatches();

      // Golește nota după ce importul a pornit cu succes
      setImportNotes((prev) => ({ ...prev, [source]: "" }));

      // Iter51 E25b — delegate polling to the reusable helper.
      if (response.data.batch_id) {
        pollBatchUntilDone(response.data.batch_id, {
          isSlowSource: source === "salesforce",
          startedAt: Date.now(),
          showToastOnFinish: true,
        });
      }
    } catch (error) {
      // Iter51 Etapa 22 — clearer error reporting; reload batches list anyway
      // in case the upload actually started on the server before the connection dropped.
      const detail = error.response?.data?.detail;
      const isTimeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
      const isNetwork = !error.response && /Network|aborted|ECONN/i.test(error.message || "");
      if (isTimeout) {
        toast.error("Upload prea lent — fișierul e prea mare sau conexiunea lentă. Verifică în Istoric Importuri dacă a fost totuși procesat.", { duration: 8000 });
      } else if (isNetwork) {
        toast.error("Conexiune întreruptă în timpul upload-ului. Verifică în Istoric Importuri dacă a fost totuși procesat.", { duration: 8000 });
      } else {
        toast.error(detail || `Eroare la import: ${error.message || "necunoscută"}`);
      }
      setImportResult(null);
      // Refresh batches list so user can see if upload partially succeeded
      try { fetchBatches(); } catch {}
    } finally {
      setUploading(false);
      setProgress(0);
      // Iter51 E33 — reset file input only if we got an event (legacy path).
      // When invoked from ImportFlow with a File, there's no input to reset.
      if (e && e.target && typeof e.target.value !== "undefined") {
        e.target.value = '';
      }
    }
  };

  const getSourceLabel = (source) => {
    const labels = {
      easy: "Easy App",
      salesforce: "Salesforce",
      crm: "CRM Legacy"
    };
    return labels[source] || source;
  };

  const getSourceColor = (source) => {
    const colors = {
      easy: "bg-blue-100 text-blue-800",
      salesforce: "bg-purple-100 text-purple-800",
      crm: "bg-orange-100 text-orange-800"
    };
    return colors[source] || "bg-gray-100 text-gray-800";
  };

  if (!hasRole(["admin", "backoffice"])) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Acces Restricționat</h2>
          <p className="text-slate-500 mt-2">Doar administratorii și backoffice pot importa date.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Import Date</h1>
        <p className="text-slate-500 mt-1">
          Importă date din Easy, Salesforce sau CRM pentru a actualiza contractele
        </p>
      </div>

      {/* Import Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="easy" data-testid="tab-easy">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Easy App
          </TabsTrigger>
          <TabsTrigger value="salesforce" data-testid="tab-salesforce">
            <Database className="h-4 w-4 mr-2" />
            Salesforce
          </TabsTrigger>
          <TabsTrigger value="crm" data-testid="tab-crm">
            <FileText className="h-4 w-4 mr-2" />
            CRM Legacy
          </TabsTrigger>
        </TabsList>

        {/* Easy App Import */}
        <TabsContent value="easy" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                </div>
                Import din Easy App
              </CardTitle>
              <CardDescription>
                Importă contracte noi semnate digital (D2D, Shop, Telesales). 
                Sistemul va crea clienți și contracte noi, sau va actualiza pe cele existente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>Câmpuri importate:</strong> CNP/CUI, Nume client, POD, NLC, Tip ofertă, 
                Zonă distribuție, Marca agent, Canal vânzare, Data semnare
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700" data-testid="identity-rule-note-easy">
                <strong>Regula de identitate (Iter67/67b, comună Easy / Salesforce / CRM Legacy / manual):</strong> POD-ul identifică locul de consum, nu contractul.
                1) Identificatorul propriu al sursei (ID Easy, nr. SF, nr. contract) are prioritate și actualizează trasabil același contract, chiar dacă pe POD există un contract mai nou.
                2) Pe POD cunoscut fără identificatorul sursei pe contract: NU se leagă automat — POD + client + energie + semnare apropiată nu demonstrează aceeași vânzare (fereastra de 45 zile doar marchează candidații). Rândul intră în „Necesită verificare”; BO leagă explicit (Reconciliere date → „Leagă rândul la contract”, cu justificare, auditat), apoi reimportul se leagă prin identificator.
                3) Contract nou pe POD existent: Easy — doar cu încetare confirmată SF a contractului anterior, același client/energie și cronologie ulterioară (contract succesiv, legat de predecesor, fără CSD/ID SF/încetare moștenite); Salesforce — ID SF nou doar dacă toate contractele SF de pe POD au încetare confirmată și cronologie compatibilă, cu „Creează rânduri noi” activ; altfel verificare.
                4) Orice altceva (client diferit, date contradictorii, mai mulți candidați, ID necunoscut fără creare) → rândul rămâne în coada „Necesită verificare” (Reconciliere date → Identitate de verificat) cu fișier/rând, identificatori, candidați și motiv — nimic nu se suprascrie. „Ignorat” nu înseamnă rezolvat.
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="import-note-easy" className="text-sm font-medium text-slate-700">
                  Notă import <span className="text-slate-400 font-normal">(opțional)</span>
                </Label>
                <Textarea
                  id="import-note-easy"
                  data-testid="import-note-easy"
                  value={importNotes.easy}
                  onChange={(e) => setImportNotes((p) => ({ ...p, easy: e.target.value }))}
                  placeholder="Ex: Contracte semnate în luna iunie — echipa Cluj. Apare sub import în istoric."
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>

              <ImportFlow
                source="easy"
                accept=".xlsx,.xls"
                colorScheme="blue"
                showPreflight={true}
                showDryRun={false}
                onRealImport={(file) => handleFileUpload(file, "easy")}
                onPreflight={(file) => handleSfPreflight(file, "easy")}
                busy={uploading && activeTab === "easy"}
              />

              {uploading && activeTab === "easy" && (
                <div className="space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-slate-500 text-center">Se procesează... {progress}%</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salesforce Import */}
        <TabsContent value="salesforce" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Database className="h-4 w-4 text-purple-600" />
                </div>
                Import din Salesforce
              </CardTitle>
              <CardDescription>
                Actualizează statusul contractelor existente. Nu creează contracte noi, 
                doar actualizează informațiile pentru POD-urile deja în sistem.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-800">
                <strong>Câmpuri actualizate:</strong> Status contract, Fază dosar, Quality Call, 
                Motiv anulare, Produs comercial, Distribuitor
              </div>

              {/* Iter51 Etapa 6 — opt-in: permite crearea contractelor noi */}
              <label className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 px-4 py-3 transition-colors ${sfAllowCreate ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100/60" : "border-amber-300 bg-amber-50 hover:bg-amber-100/60"}`} data-testid="sf-allow-create-label">
                <input
                  type="checkbox"
                  checked={sfAllowCreate}
                  onChange={(e) => setSfAllowCreate(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                  data-testid="sf-allow-create-checkbox"
                />
                <span className="text-sm flex-1">
                  <span className={`font-semibold ${sfAllowCreate ? "text-emerald-900" : "text-amber-900"}`}>Permite crearea contractelor noi {sfAllowCreate ? "✓ activat" : ""}</span>
                  <span className={`block mt-0.5 ${sfAllowCreate ? "text-emerald-800" : "text-amber-800"}`}>
                    Pentru POD-urile necunoscute în sistem, importul va crea automat Client + Contract + Sale minimal pe baza datelor din SF.
                    Fără această opțiune, rândurile cu POD necunoscut sunt sărite (skipped).
                  </span>
                  {!sfAllowCreate && (
                    <span className="block mt-2 px-2 py-1.5 rounded bg-red-100 border border-red-300 text-red-900 text-xs font-medium" data-testid="sf-allow-create-warning">
                      ⚠️ Atenție: dacă baza de date e goală (primul import sau după reset), TOATE rândurile vor fi sărite fără această opțiune.
                    </span>
                  )}
                </span>
              </label>

              <div className="space-y-1.5">
                <Label htmlFor="import-note-sf" className="text-sm font-medium text-slate-700">
                  Notă import <span className="text-slate-400 font-normal">(opțional)</span>
                </Label>
                <Textarea
                  id="import-note-sf"
                  data-testid="import-note-salesforce"
                  value={importNotes.salesforce}
                  onChange={(e) => setImportNotes((p) => ({ ...p, salesforce: e.target.value }))}
                  placeholder="Ex: Actualizare status SF — export 19 iunie. Apare sub import în istoric."
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>

              <ImportFlow
                source="salesforce"
                accept=".xlsx,.xls"
                colorScheme="purple"
                showPreflight={true}
                showDryRun={true}
                onRealImport={(file) => handleFileUpload(file, "salesforce")}
                onPreflight={(file) => handleSfPreflight(file)}
                onDryRun={(file) => handleSfSimulate(file)}
                busy={uploading || simulating || preflighting}
                extraConfirmHint={
                  !sfAllowCreate
                    ? "⚠️ Opțiunea „Permite crearea contractelor noi” NU este bifată — rândurile cu POD necunoscut vor fi sărite."
                    : "Opțiunea „Permite crearea contractelor noi” ESTE bifată — Client/Contract/Sale vor fi create pentru POD-uri necunoscute."
                }
              />

              {uploading && activeTab === "salesforce" && (
                <div className="space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-slate-500 text-center">Se procesează... {progress}%</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRM Legacy Import */}
        <TabsContent value="crm" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-orange-600" />
                </div>
                Import din CRM Legacy
              </CardTitle>
              <CardDescription>
                Importă date din sistemul CRM vechi. Suportă formatul .xls.
                Creează sau actualizează clienți și contracte.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800">
                <strong>Câmpuri importate:</strong> CNP, Nume, Prenume, POD, NLC, Tip ofertă, 
                Județ, Localitate, Agenție, Agent, Operator BO
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700" data-testid="identity-rule-note-crm">
                <strong>Regula de identitate (Iter67/67b, comună Easy / Salesforce / CRM Legacy / manual):</strong> POD-ul identifică locul de consum, nu contractul.
                1) Identificatorul propriu al sursei (ID Easy, nr. SF, nr. contract) are prioritate și actualizează trasabil același contract, chiar dacă pe POD există un contract mai nou.
                2) Pe POD cunoscut fără identificatorul sursei pe contract: NU se leagă automat — POD + client + energie + semnare apropiată nu demonstrează aceeași vânzare (fereastra de 45 zile doar marchează candidații). Rândul intră în „Necesită verificare”; BO leagă explicit (Reconciliere date → „Leagă rândul la contract”, cu justificare, auditat), apoi reimportul se leagă prin identificator.
                3) Contract nou pe POD existent: Easy — doar cu încetare confirmată SF a contractului anterior, același client/energie și cronologie ulterioară (contract succesiv, legat de predecesor, fără CSD/ID SF/încetare moștenite); Salesforce — ID SF nou doar dacă toate contractele SF de pe POD au încetare confirmată și cronologie compatibilă, cu „Creează rânduri noi” activ; altfel verificare.
                4) Orice altceva (client diferit, date contradictorii, mai mulți candidați, ID necunoscut fără creare) → rândul rămâne în coada „Necesită verificare” (Reconciliere date → Identitate de verificat) cu fișier/rând, identificatori, candidați și motiv — nimic nu se suprascrie. „Ignorat” nu înseamnă rezolvat.
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="import-note-crm" className="text-sm font-medium text-slate-700">
                  Notă import <span className="text-slate-400 font-normal">(opțional)</span>
                </Label>
                <Textarea
                  id="import-note-crm"
                  data-testid="import-note-crm"
                  value={importNotes.crm}
                  onChange={(e) => setImportNotes((p) => ({ ...p, crm: e.target.value }))}
                  placeholder="Ex: Export CRM legacy septembrie 2025. Apare sub import în istoric."
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>

              <ImportFlow
                source="crm"
                accept=".xlsx,.xls"
                colorScheme="orange"
                showPreflight={true}
                showDryRun={false}
                onRealImport={(file) => handleFileUpload(file, "crm")}
                onPreflight={(file) => handleSfPreflight(file, "crm")}
                busy={uploading && activeTab === "crm"}
              />

              {uploading && activeTab === "crm" && (
                <div className="space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-slate-500 text-center">Se procesează... {progress}%</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import History */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Istoric Importuri</CardTitle>
            <CardDescription>
              Ultimele importuri efectuate (afișează {batches.length} / max {historyLimit})
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Input
                placeholder="Caută după nume fișier..."
                value={historyFilenameQuery}
                onChange={(e) => setHistoryFilenameQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchBatches(undefined, e.target.value); }}
                className="w-56 h-9 text-xs"
                data-testid="history-filename-search"
              />
            </div>
            <Select
              value={historyStatusFilter}
              onValueChange={(v) => { setHistoryStatusFilter(v); fetchBatches(v); }}
              data-testid="history-status-filter"
            >
              <SelectTrigger className="w-44 h-9 text-xs">
                <SelectValue placeholder="Filtru status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate statusurile</SelectItem>
                <SelectItem value="processing">Doar în progres</SelectItem>
                <SelectItem value="interrupted">Doar întrerupte</SelectItem>
                <SelectItem value="completed">Doar finalizate</SelectItem>
                <SelectItem value="partial">Doar parțiale</SelectItem>
                <SelectItem value="cancelled">Doar oprite</SelectItem>
                <SelectItem value="failed">Doar eșuate</SelectItem>
              </SelectContent>
            </Select>
            {historyLimit < 200 && (
              <Button
                variant="outline" size="sm"
                onClick={() => { setHistoryLimit(200); fetchBatches(undefined, undefined, 200); }}
                data-testid="history-expand-limit"
              >
                Arată 200
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => fetchBatches()} disabled={loadingBatches}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingBatches ? 'animate-spin' : ''}`} />
              Reîmprospătează
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingBatches ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Upload className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Nu există importuri anterioare</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <input
                ref={resumeFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleResumeFileSelected}
                data-testid="resume-file-input"
              />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sursă</TableHead>
                    <TableHead>Fișier</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Create</TableHead>
                    <TableHead className="text-center">Actualizate</TableHead>
                    <TableHead className="text-center">Ignorate</TableHead>
                    <TableHead className="text-center">Erori</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-center">Raport</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow
                      key={batch.id}
                      id={`batch-row-${batch.id}`}
                      className={highlightBatchId === batch.id ? "bg-blue-50 ring-2 ring-inset ring-blue-300" : undefined}
                      data-testid={`batch-row-${batch.id}`}
                    >
                      <TableCell>
                        <Badge className={getSourceColor(batch.source)}>
                          {getSourceLabel(batch.source)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[240px]" title={batch.filename}>
                        <div className="truncate font-medium">{batch.filename}</div>
                        {batch.note && (
                          <div
                            className="mt-0.5 flex items-start gap-1 text-xs text-slate-500 italic whitespace-pre-wrap break-words"
                            data-testid={`batch-note-${batch.id}`}
                            title={batch.note}
                          >
                            <StickyNote className="h-3 w-3 mt-0.5 shrink-0 text-slate-400" />
                            <span>{batch.note}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {batch.status === "processing" ? (
                          <div className="flex items-center gap-1.5 justify-center">
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors text-xs font-medium ${
                                batch.is_stale
                                  ? "bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100"
                                  : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                              }`}
                              onClick={() => handleViewProgress(batch)}
                              data-testid={`view-progress-${batch.id}`}
                              title={batch.is_stale
                                ? "Posibil oprit (server restart). Click pentru detalii."
                                : "Vezi progresul live cu ETA"}
                            >
                              {batch.is_stale ? (
                                <AlertCircle className="h-3 w-3" />
                              ) : (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              )}
                              {batch.is_stale ? "Posibil oprit" : "În progres"}
                            </button>
                            {hasRole(["admin", "backoffice"]) && (
                              <button
                                type="button"
                                className={`inline-flex items-center justify-center px-2 py-1 rounded-md border text-xs ${
                                  batch.is_stale
                                    ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                                }`}
                                onClick={() => handleMarkBatchFailed(batch)}
                                data-testid={`mark-failed-${batch.id}`}
                                title="Marchează importul ca eșuat (forțat). Folosește dacă e blocat / oprit de un restart de server. Datele deja salvate se păstrează."
                              >
                                Curăță
                              </button>
                            )}
                            {renderResumeButton(batch)}
                            {hasRole(["admin", "backoffice"]) && !batch.cancellation_requested && !batch.is_stale && (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                                onClick={() => setCancelTarget(batch)}
                                data-testid={`cancel-import-${batch.id}`}
                                title="Oprește importul"
                              >
                                <Square className="h-3 w-3 fill-current" />
                              </button>
                            )}
                            {batch.cancellation_requested && batch.is_stale && hasRole(["admin", "backoffice"]) && (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 text-xs"
                                onClick={() => handleMarkBatchFailed(batch)}
                                data-testid={`cleanup-cancelled-${batch.id}`}
                                title="Oprire blocată (server restart?). Click pentru a marca batch-ul ca eșuat."
                              >
                                <AlertCircle className="h-3 w-3" />
                                Oprire blocată — Curăță
                              </button>
                            )}
                            {batch.cancellation_requested && !batch.is_stale && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium"
                                title="Oprire în curs..."
                                data-testid={`cancel-pending-${batch.id}`}
                              >
                                <Ban className="h-3 w-3" />
                                Oprire...
                              </span>
                            )}
                          </div>
                        ) : batch.status === "completed" ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Finalizat</Badge>
                        ) : batch.status === "partial" ? (
                          <div className="flex items-center gap-1.5 justify-center">
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Parțial</Badge>
                            {renderResumeButton(batch)}
                          </div>
                        ) : batch.status === "interrupted" ? (
                          <>
                          <div className="flex items-center gap-1.5 justify-center">
                            <Badge
                              className="bg-orange-100 text-orange-700 border-orange-200 cursor-help"
                              data-testid={`status-interrupted-${batch.id}`}
                              title={batch.interrupted
                                ? `${batch.interrupted.reason}\nUltimul progres: ${batch.interrupted.last_progress_at ? new Date(batch.interrupted.last_progress_at).toLocaleString("ro-RO") : "—"}\nDetectat: ${batch.interrupted.detected_at ? new Date(batch.interrupted.detected_at).toLocaleString("ro-RO") : "—"}\nProcesate: ${batch.interrupted.rows_processed}/${batch.interrupted.rows_total}`
                                : "Import întrerupt (worker oprit)"}
                            >
                              <AlertCircle className="h-3 w-3 mr-1 inline" />
                              Întrerupt
                            </Badge>
                            <button
                              type="button"
                              className="text-[11px] text-blue-600 hover:underline"
                              onClick={() => showResumePlan(batch)}
                              data-testid={`resume-plan-${batch.id}`}
                              title="Previzualizare plan de reluare (nu pornește importul)"
                            >
                              Plan reluare
                            </button>
                            {renderResumeButton(batch)}
                          </div>
                          {batch.confirmed_progress && (
                            <div className="mt-1 text-[10px] text-slate-500 leading-tight text-center" data-testid={`confirmed-progress-${batch.id}`}>
                              Confirmat salvat: <strong>{batch.confirmed_progress.committed_rows}</strong> ({batch.confirmed_progress.base_counts.created} create / {batch.confirmed_progress.base_counts.updated} actualizate)
                              <br />Contoare istorice: {(batch.rows_created || 0) + (batch.rows_updated || 0) + (batch.rows_skipped || 0) + (batch.rows_errors || 0)} · nesalvate: {batch.confirmed_progress.unsaved_rows}
                            </div>
                          )}
                          </>
                        ) : batch.status === "cancelled" ? (
                          <div className="flex items-center gap-1.5 justify-center">
                            <Badge className="bg-slate-200 text-slate-700 border-slate-300" data-testid={`status-cancelled-${batch.id}`}>
                              <Ban className="h-3 w-3 mr-1 inline" />
                              Oprit
                            </Badge>
                            {renderResumeButton(batch)}
                          </div>
                        ) : batch.status === "failed" ? (
                          <div className="flex items-center gap-1.5 justify-center">
                            <Badge className="bg-red-100 text-red-700 border-red-200">Eșuat</Badge>
                            {renderResumeButton(batch)}
                          </div>
                        ) : (
                          <Badge variant="outline">{batch.status || "—"}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {batch.rows_total}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-emerald-600 font-medium">+{batch.rows_created}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-blue-600 font-medium">{batch.rows_updated}</span>
                      </TableCell>
                      <TableCell className="text-center text-slate-500">
                        {batch.rows_skipped}
                      </TableCell>
                      <TableCell className="text-center">
                        {batch.rows_errors > 0 ? (
                          <button
                            type="button"
                            className="text-red-600 font-medium hover:underline hover:text-red-800"
                            onClick={() => setErrorDetailsBatch(batch)}
                            data-testid={`view-errors-${batch.id}`}
                            title="Click pentru a vedea detaliile erorilor"
                          >
                            {batch.rows_errors}
                          </button>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        <div>{batch.imported_at ? new Date(batch.imported_at).toLocaleString('ro-RO') : '-'}</div>
                        {batch.duration_seconds != null && batch.duration_seconds > 0 && (
                          <div
                            className="text-[11px] text-slate-400"
                            data-testid={`batch-duration-${batch.id}`}
                            title="Durata importului (viteza medie)"
                          >
                            ⏱ {batch.duration_seconds >= 60
                              ? `${Math.floor(batch.duration_seconds / 60)}m ${Math.round(batch.duration_seconds % 60)}s`
                              : `${Math.round(batch.duration_seconds)}s`}
                            {batch.rows_total > 0 && ` · ${(batch.rows_total / batch.duration_seconds).toFixed(1)} rânduri/s`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {batch.has_report ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 text-xs"
                              onClick={() => handleDownloadReport(batch)}
                              data-testid={`download-report-${batch.id}`}
                              title="Descarcă fișierul original cu coloane suplimentare: Status import, Motiv, ID entitate"
                            >
                              <Download className="h-3 w-3" />
                              Raport
                            </button>
                          ) : null}
                          {/* Iter52 P10 — Vezi modificările aplicate de acest batch */}
                          {(batch.status === "completed" || batch.status === "partial") && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-xs"
                              onClick={() => setBatchChangesTarget(batch)}
                              data-testid={`view-changes-${batch.id}`}
                              title="Vezi câmpurile efectiv modificate de acest import (Field History + Status History)"
                            >
                              <Activity className="h-3 w-3" />
                              Modificări
                            </button>
                          )}
                          {!batch.has_report && batch.status !== "completed" && batch.status !== "partial" && (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.status === "processing" ? (
                <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
              ) : importResult?.rows_errors === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : importResult?.status === "failed" ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              {importResult?.status === "processing" ? "Procesare în curs..." : "Rezultat Import"}
            </DialogTitle>
            <DialogDescription>
              {importResult?.status === "processing"
                ? "Importul rulează în fundal. Poți închide acest dialog — vei vedea rezultatul final în Istoric."
                : "Rezumatul operațiunii de import"}
            </DialogDescription>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-4 py-4">
              {/* Iter53 P36 — live progress via lightweight endpoint + reusable widget */}
              {importResult.status === "processing" && importResult.batch_id && (
                <ImportProgressWidget
                  batchId={importResult.batch_id}
                  onFinish={(payload) => {
                    // Sync final counters into importResult and refresh history list
                    setImportResult((prev) => ({
                      ...(prev || {}),
                      status: payload.status,
                      rows_total: payload.rows_total,
                      rows_created: payload.rows_created,
                      rows_updated: payload.rows_updated,
                      rows_skipped: payload.rows_skipped,
                      rows_errors: payload.rows_errors,
                    }));
                    setProgress(100);
                    fetchBatches();
                  }}
                  onCancel={() => setCancelTarget({ id: importResult.batch_id, filename: importResult.filename })}
                  compact
                  showTitle={false}
                  testIdPrefix="import-live-progress"
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">{importResult.rows_total}</p>
                  <p className="text-sm text-slate-500">Total rânduri</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">+{importResult.rows_created}</p>
                  <p className="text-sm text-slate-500">Create</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.rows_updated}</p>
                  <p className="text-sm text-slate-500">Actualizate</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-400">{importResult.rows_skipped}</p>
                  <p className="text-sm text-slate-500">Ignorate</p>
                </div>
              </div>

              {importResult.rows_errors > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-800 mb-2">
                    {importResult.rows_errors} erori detectate
                  </p>
                  <div className="max-h-32 overflow-y-auto text-sm text-red-600 space-y-1">
                    {importResult.errors?.slice(0, 5).map((err, idx) => (
                      <p key={idx}>Rând {err.row}: {err.error}</p>
                    ))}
                  </div>
                </div>
              )}

              {importResult.rows_unassigned > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="unassigned-warning">
                  <p className="font-medium text-red-800 mb-1">
                    {importResult.rows_unassigned} vânzări neasociate
                  </p>
                  <p className="text-xs text-red-700">
                    Aceste vânzări nu au cod agent recunoscut și au fost marcate <strong>needs_reconciliation</strong>.
                    Nu sunt vizibile pentru agenți. Setează codul agent în pagina Utilizatori și reconciliază (Batch 3).
                  </p>
                </div>
              )}

              {importResult.ownership_conflicts > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="ownership-conflict-warning">
                  <p className="font-medium text-orange-800 mb-1">
                    {importResult.ownership_conflicts} conflicte de ownership detectate
                  </p>
                  <p className="text-xs text-orange-700">
                    Contracte deja existente au fost re-importate cu un cod agent diferit. Ownerul actual a fost păstrat;
                    vânzările afectate au fost marcate needs_reconciliation și loguite în Audit.
                  </p>
                </div>
              )}

              {importResult.unmatched_agent_codes && importResult.unmatched_agent_codes.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="unmatched-codes-warning">
                  <p className="font-medium text-amber-800 mb-1">
                    Coduri agent neasociate ({importResult.unmatched_agent_codes.length})
                  </p>
                  <p className="text-xs text-amber-700 mb-2">
                    Aceste vânzări au fost atribuite contului care a rulat importul. Setează codurile de agent (Marca) în pagina Utilizatori pentru ca agenții să vadă vânzările lor.
                  </p>
                  <div className="max-h-24 overflow-y-auto text-xs text-amber-700 flex flex-wrap gap-1">
                    {importResult.unmatched_agent_codes.slice(0, 30).map((code, idx) => (
                      <span key={idx} className="bg-amber-100 px-2 py-0.5 rounded">{code}</span>
                    ))}
                  </div>
                </div>
              )}

              {importResult.matched_agents_count > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="matched-agents-info">
                  <strong>{importResult.matched_agents_count}</strong> agenți identificați și asociați automat cu vânzările lor.
                </div>
              )}

              {/* Iter50 — Client summary: new / updated / top changed fields */}
              {importResult.client_summary && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="client-summary-card">
                  <p className="font-medium text-indigo-900 mb-2">Sumar clienți</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-xl font-bold text-emerald-600">+{importResult.client_summary.created || 0}</p>
                      <p className="text-xs text-slate-600">Clienți noi</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-xl font-bold text-blue-600">{importResult.client_summary.updated || 0}</p>
                      <p className="text-xs text-slate-600">Clienți actualizați</p>
                    </div>
                  </div>
                  {Array.isArray(importResult.client_summary.top_changed_fields) && importResult.client_summary.top_changed_fields.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-indigo-800 mb-1.5">Top câmpuri modificate</p>
                      <div className="space-y-1">
                        {importResult.client_summary.top_changed_fields.map((tf, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs bg-white rounded px-2 py-1"
                            data-testid={`client-field-change-${tf.field}`}
                          >
                            <span className="font-mono text-slate-700">{tf.field}</span>
                            <span className="font-semibold text-indigo-700">{tf.count} client{tf.count !== 1 ? "i" : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button 
                className="w-full" 
                onClick={() => setShowResultDialog(false)}
                data-testid="close-result-btn"
              >
                Închide
              </Button>
            </div>
          )}
          {/* Iter51 E26 — Stop import button while processing */}
          {importResult?.status === "processing" && hasRole(["admin", "backoffice"]) && (
            <DialogFooter className="border-t pt-3">
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setCancelTarget({ id: importResult.batch_id, filename: importResult.filename })}
                disabled={importResult?.cancellation_requested}
                data-testid="dialog-cancel-import-btn"
              >
                <Square className="h-4 w-4 mr-2 fill-current" />
                {importResult?.cancellation_requested ? "Oprire în curs..." : "Oprește importul"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter51 Etapa 20 — Error Details viewer */}
      <Dialog open={!!errorDetailsBatch} onOpenChange={(o) => !o && setErrorDetailsBatch(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="error-details-dialog">
          <DialogHeader>
            <DialogTitle>Detalii erori import</DialogTitle>
            <DialogDescription>
              {errorDetailsBatch?.filename} · {errorDetailsBatch?.rows_errors} erori din {errorDetailsBatch?.rows_total} rânduri
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 px-1">
            {loadingErrorDetails && (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="error-details-loading">Se încarcă detaliile...</p>
            )}
            {errorDetailsFull && !loadingErrorDetails && (() => {
              const ed = errorDetailsFull.error_details || {};
              const diag = ed.diagnostic || {};
              const rowErrors = ed.row_errors || [];
              const phaseErrors = ed.phase_errors || [];
              const autoCreated = ed.auto_created || {};
              return (
                <>
                  {/* Counts summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-center">
                      <p className="text-slate-500">Create</p>
                      <p className="text-lg font-semibold text-emerald-700">{errorDetailsFull.rows_created}</p>
                    </div>
                    <div className="rounded border border-blue-200 bg-blue-50 p-2 text-center">
                      <p className="text-slate-500">Actualizate</p>
                      <p className="text-lg font-semibold text-blue-700">{errorDetailsFull.rows_updated}</p>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 p-2 text-center">
                      <p className="text-slate-500">Ignorate</p>
                      <p className="text-lg font-semibold text-slate-700">{errorDetailsFull.rows_skipped}</p>
                    </div>
                    <div className="rounded border border-red-200 bg-red-50 p-2 text-center">
                      <p className="text-slate-500">Erori</p>
                      <p className="text-lg font-semibold text-red-700">{errorDetailsFull.rows_errors}</p>
                    </div>
                  </div>

                  {/* Auto-created */}
                  {Object.keys(autoCreated).length > 0 && (
                    <div className="rounded border border-emerald-200 bg-emerald-50 p-3" data-testid="error-details-auto-created">
                      <p className="text-xs uppercase tracking-wider font-semibold text-emerald-900 mb-1">Entități auto-create</p>
                      <p className="text-sm text-emerald-800">
                        Clients: <b>{autoCreated.clients || 0}</b> · Contracts: <b>{autoCreated.contracts || 0}</b> · Sales: <b>{autoCreated.sales || 0}</b>
                      </p>
                    </div>
                  )}

                  {/* Diagnostic */}
                  {diag.detected_columns && (
                    <details className="rounded border border-slate-200 p-2" data-testid="error-details-diagnostic">
                      <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                        Diagnostic ({diag.rows_in_file} rânduri în fișier, POD column: <code className="bg-slate-100 px-1 rounded text-xs">{diag.pod_column_resolved || "—"}</code>)
                      </summary>
                      <div className="mt-2 text-xs">
                        <p className="text-slate-500 mb-1">Coloane detectate (primele {diag.detected_columns.length}):</p>
                        <div className="flex flex-wrap gap-1">
                          {diag.detected_columns.map((c, i) => (
                            <span key={i} className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-mono text-[10px]">{c}</span>
                          ))}
                        </div>
                      </div>
                    </details>
                  )}

                  {/* Phase errors */}
                  {phaseErrors.length > 0 && (
                    <div className="rounded border border-orange-200 bg-orange-50 p-3" data-testid="error-details-phase">
                      <p className="text-xs uppercase tracking-wider font-semibold text-orange-900 mb-2">Erori de fază ({phaseErrors.length})</p>
                      <ul className="space-y-1 text-xs">
                        {phaseErrors.map((p, i) => (
                          <li key={i} className="text-orange-800"><b>{p.phase}</b>: {p.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Row errors */}
                  <div data-testid="error-details-rows">
                    <p className="text-sm font-semibold text-slate-900 mb-2">
                      Erori per rând ({rowErrors.length}{ed.row_errors_total > rowErrors.length ? ` / ${ed.row_errors_total} total — afișate primele ${rowErrors.length}` : ""})
                    </p>
                    {rowErrors.length === 0 ? (
                      <p className="text-xs text-slate-500">Nicio eroare de rând capturată.</p>
                    ) : (
                      <div className="border border-slate-200 rounded overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="text-left p-2 w-12">Rând</th>
                              <th className="text-left p-2">POD</th>
                              <th className="text-left p-2">Contract Nr</th>
                              <th className="text-left p-2">Tip eroare</th>
                              <th className="text-left p-2">Mesaj</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowErrors.map((e, i) => (
                              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="p-2 font-mono text-slate-700">{e.row || "—"}</td>
                                <td className="p-2 font-mono text-[10px] truncate max-w-[120px]" title={e.pod}>{e.pod || "—"}</td>
                                <td className="p-2 font-mono text-[10px]">{e.contract_number || "—"}</td>
                                <td className="p-2"><Badge className="bg-red-100 text-red-800 text-[10px]">{e.error_type || "Error"}</Badge></td>
                                <td className="p-2 text-red-700 max-w-[400px] truncate" title={e.error}>{e.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => {
              const blob = new Blob([JSON.stringify(errorDetailsFull, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = `import-errors-${errorDetailsBatch?.id?.slice(0,8)}.json`; a.click();
              URL.revokeObjectURL(url);
            }} data-testid="download-error-json">Descarcă JSON</Button>
            <Button onClick={() => setErrorDetailsBatch(null)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter51 E27 — Dry-Run Simulation Results Dialog */}
      <Dialog open={!!simulationResult} onOpenChange={(o) => !o && setSimulationResult(null)}>
        <DialogContent className="max-w-2xl" data-testid="simulation-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-indigo-600" />
              Rezultat simulare
            </DialogTitle>
            <DialogDescription className="text-indigo-900">
              <span className="font-mono text-xs bg-indigo-50 px-2 py-0.5 rounded">{simulationResult?.filename}</span>
              {" · "}
              <span className="font-semibold">{simulationResult?.rows_total} rânduri</span>
              {" · "}
              <span className={simulationResult?.allow_create ? "text-emerald-700" : "text-amber-700"}>
                allow_create = {simulationResult?.allow_create ? "ON" : "OFF"}
              </span>
            </DialogDescription>
          </DialogHeader>
          {simulationResult && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-amber-900 text-sm flex items-start gap-2">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Simulare — nimic nu a fost salvat în DB.</p>
                  <p className="text-xs mt-0.5">{simulationResult.message}</p>
                </div>
              </div>

              {/* 3 totals summary — Iter68i: unitate = rânduri; „actualizate” = modificare efectiv propusă */}
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="sim-total-create">
                  <p className="text-xs text-emerald-700">Rânduri de creat</p>
                  <p className="text-2xl font-bold text-emerald-700">{simulationResult.will_create?.total || 0}</p>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center" data-testid="sim-total-update">
                  <p className="text-xs text-blue-700">Rânduri cu modificare propusă</p>
                  <p className="text-2xl font-bold text-blue-700">{simulationResult.will_update?.total || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center" data-testid="sim-total-skip">
                  <p className="text-xs text-slate-600">Rânduri sărite</p>
                  <p className="text-2xl font-bold text-slate-700">{simulationResult.will_skip?.total || 0}</p>
                </div>
              </div>
              {simulationResult.unit === 'rows' && (
                <div className="grid grid-cols-2 gap-2 text-sm" data-testid="sim-identity-csd">
                  <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-1" data-testid="sim-row-results">
                    <p className="text-xs font-semibold text-slate-700">Rezultatul rândului (contoare pe rânduri, nu contracte unice)</p>
                    <p className="text-xs text-slate-600">Potrivite fără modificare (câmpuri evaluate: {(simulationResult.evaluated_fields || []).join(', ')}): <strong data-testid="sim-match-no-change">{simulationResult.will_match_no_change?.total || 0}</strong></p>
                    <p className="text-xs text-violet-700">Potrivite, dar modificări neevaluate (VAS/HVAS fără POD, pe dosar existent — CSD neevaluat): <strong data-testid="sim-match-unevaluated">{simulationResult.will_match_unevaluated?.total || 0}</strong></p>
                    <p className="text-xs text-amber-700">Necesită verificare identitate (POD/ID/client contradictoriu — fără scriere): <strong data-testid="sim-review">{simulationResult.will_review?.total || 0}</strong></p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-1" data-testid="sim-csd-decisions">
                    <p className="text-xs font-semibold text-slate-700">Decizia CSD (numai din AL, separat de restul câmpurilor)</p>
                    <p className="text-xs text-emerald-700">Modificare propusă: <strong data-testid="sim-csd-change">{simulationResult.csd?.proposed_change || 0}</strong></p>
                    <p className="text-xs text-slate-600">Valoare păstrată — identică: <strong>{simulationResult.csd?.kept_same || 0}</strong> · cu motiv (POD diferit / raport mai vechi): <strong data-testid="sim-csd-kept-reason">{simulationResult.csd?.kept_with_reason || 0}</strong> · fără AL: <strong>{simulationResult.csd?.no_al || 0}</strong></p>
                    <p className="text-xs text-amber-700">Necesită verificare (proveniență/versiune/alt caz): <strong data-testid="sim-csd-review">{simulationResult.csd?.review_required || 0}</strong></p>
                    <p className="text-[11px] text-slate-500">Versiune raport: {simulationResult.source_version?.kind === 'report_generated_at' ? simulationResult.source_version.value : 'necunoscută (fără metadate SF în preambul)'}</p>
                  </div>
                </div>
              )}

              {/* Per-kind table */}
              <div className="rounded-lg border border-slate-200 overflow-hidden text-sm">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Tip</th>
                      <th className="text-right px-3 py-2">Create</th>
                      <th className="text-right px-3 py-2">Update</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Iter52 P13g — categorii aliniate cu Products & Offers (fără Anulat — e status) */}
                    {["EE", "Gaz", "VAS", "HVAS", "Combo EE + VAS", "Combo Gaz + VAS", "Cancelled"].map((k) => {
                      const c = simulationResult.will_create?.[k] || 0;
                      const u = simulationResult.will_update?.[k] || 0;
                      const t = c + u;
                      if (t === 0) return null;
                      return (
                        <tr key={k} className="border-t border-slate-100" data-testid={`sim-row-${k}`}>
                          <td className="px-3 py-1.5 font-medium">
                            {k === "Cancelled" ? "Anulări" : k}
                          </td>
                          <td className="text-right px-3 py-1.5 text-emerald-700 font-mono">{c || ""}</td>
                          <td className="text-right px-3 py-1.5 text-blue-700 font-mono">{u || ""}</td>
                          <td className="text-right px-3 py-1.5 font-semibold font-mono">{t}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Skip breakdown */}
              {(simulationResult.will_skip?.total || 0) > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs space-y-1" data-testid="sim-skip-breakdown">
                  <p className="font-semibold text-amber-900 mb-1.5">Motive pentru rândurile sărite:</p>
                  {simulationResult.will_skip?.no_match_no_allow_create > 0 && (
                    <p>• <strong>{simulationResult.will_skip.no_match_no_allow_create}</strong> POD necunoscut în DB (bifează „Permite crearea contractelor noi" pentru a le importa).</p>
                  )}
                  {simulationResult.will_skip?.no_pod_no_kind > 0 && (
                    <p>• <strong>{simulationResult.will_skip.no_pod_no_kind}</strong> rând fără POD, fără tip detectabil (probabil header sau total în Excel).</p>
                  )}
                  {simulationResult.will_skip?.missing_dossier > 0 && (
                    <p>• <strong>{simulationResult.will_skip.missing_dossier}</strong> rând VAS/HVAS/anulat fără Dossier Code (nu poate fi identificat unic).</p>
                  )}
                </div>
              )}

              {/* Iter52 P9 — Universal POD + category breakdown banner */}
              <ImportCategoryBanner
                data={simulationResult}
                onMapUnknown={isAdmin ? (v) => openMapModal(v, "simulate") : undefined}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSimulationResult(null)} data-testid="sim-close-btn">
              Închide
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                setSimulationResult(null);
                toast.info('Pentru import real, încarcă fișierul prin butonul „Selectează fișier Excel" de mai sus.', { duration: 6000 });
              }}
              data-testid="sim-proceed-real"
            >
              Am verificat — vreau import real
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter51 E30 — Pre-flight results dialog */}
      <Dialog open={!!preflightResult} onOpenChange={(o) => !o && setPreflightResult(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="preflight-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-teal-600" />
              Pre-flight: verificare header & calitate date
            </DialogTitle>
            <DialogDescription className="space-y-1">
              <span>Fișier: </span>
              <span className="font-mono text-xs bg-teal-50 px-2 py-0.5 rounded">{preflightResult?.filename}</span>
              <span> · </span>
              <span className="font-semibold">{preflightResult?.rows_total} rânduri</span>
              <span> · </span>
              <span>{preflightResult?.all_headers_count} coloane</span>
            </DialogDescription>
          </DialogHeader>

          {preflightResult && (
            <div className="space-y-4">
              {/* Overall severity badge */}
              <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${
                preflightResult.overall_severity === "error"
                  ? "bg-red-50 border-red-200 text-red-900"
                  : preflightResult.overall_severity === "warning"
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-900"
              }`} data-testid="preflight-overall-banner">
                {preflightResult.overall_severity === "error" ? (
                  <XCircle className="h-5 w-5 shrink-0 mt-0.5" />
                ) : preflightResult.overall_severity === "warning" ? (
                  <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-semibold">
                    {preflightResult.overall_severity === "error" && "Probleme critice — NU porni importul"}
                    {preflightResult.overall_severity === "warning" && "Avertismente — revizuiește înainte de import"}
                    {preflightResult.overall_severity === "ok" && "Fișierul arată curat"}
                  </p>
                  <p className="text-xs mt-0.5">{preflightResult.message}</p>
                </div>
              </div>

              {/* Iter52 P9 — Universal POD + category breakdown banner */}
              <ImportCategoryBanner
                data={preflightResult}
                onMapUnknown={isAdmin ? (v) => openMapModal(v, "preflight") : undefined}
              />

              {/* Warnings list */}
              {preflightResult.warnings?.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="preflight-warnings">
                  <p className="font-semibold text-sm text-slate-900 mb-2 flex items-center gap-1.5">
                    <ListChecks className="h-4 w-4" /> Detalii ({preflightResult.warnings.length})
                  </p>
                  <ul className="space-y-1 text-xs">
                    {preflightResult.warnings.map((w, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`inline-block w-2 h-2 mt-1.5 rounded-full shrink-0 ${
                          w.severity === "error" ? "bg-red-500"
                            : w.severity === "warning" ? "bg-amber-500"
                            : w.severity === "info" ? "bg-blue-400"
                            : "bg-emerald-500"
                        }`} />
                        <span className={`flex-1 ${
                          w.severity === "error" ? "text-red-900"
                            : w.severity === "warning" ? "text-amber-900"
                            : "text-slate-700"
                        }`}>{w.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Field quality table */}
              <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="preflight-field-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="text-left px-3 py-2">Câmp</th>
                      <th className="text-left px-3 py-2">Coloană mapată</th>
                      <th className="text-right px-3 py-2">% gol</th>
                      <th className="text-right px-3 py-2">Stare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Iter61 — calculează lista de coloane O SINGURĂ DATĂ (nu per rând)
                      // și PLAFONEAZĂ la 300 opțiuni. Fișiere Excel cu coloane fantomă
                      // (până la 16384) generau zeci de mii de <option> -> freeze total.
                      const uniqueCols = Array.from(new Set([
                        ...(preflightResult.unknown_headers || []),
                        ...(preflightResult.field_quality || []).map(x => x.matched_column).filter(Boolean),
                      ])).sort().slice(0, 300);
                      return preflightResult.field_quality?.map((f) => {
                      // Iter51 E33 Faza 3 — overridden value takes precedence visually
                      const overrideVal = columnOverrides[f.label]; // undefined | null | string
                      const effectiveCol = overrideVal !== undefined ? overrideVal : f.matched_column;
                      const isIgnored = overrideVal === null;
                      const sevColor = f.severity === "error" ? "text-red-700 bg-red-50"
                        : f.severity === "warning" ? "text-amber-700 bg-amber-50"
                        : f.severity === "info" ? "text-slate-600 bg-slate-50"
                        : "text-emerald-700 bg-emerald-50";
                      // Select value: "" auto, "__IGNORE__" ignore, or col name
                      const selectValue = isIgnored ? "__IGNORE__" : (overrideVal !== undefined ? overrideVal : "");
                      return (
                        <tr key={f.label} className="border-t border-slate-100" data-testid={`preflight-row-${f.label}`}>
                          <td className="px-3 py-1.5">
                            <span className="font-medium">{f.label}</span>
                            <span className={`ml-2 text-[10px] uppercase tracking-wide ${
                              f.criticality === "critical" ? "text-red-600"
                              : f.criticality === "important" ? "text-amber-600"
                              : "text-slate-400"
                            }`}>
                              {f.criticality}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <select
                              value={selectValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                setColumnOverrides((prev) => {
                                  const next = { ...prev };
                                  if (v === "") {
                                    delete next[f.label]; // back to auto-detect
                                  } else if (v === "__IGNORE__") {
                                    next[f.label] = null;
                                  } else {
                                    next[f.label] = v;
                                  }
                                  return next;
                                });
                              }}
                              data-testid={`preflight-select-${f.label}`}
                              className={`text-[11px] font-mono px-1.5 py-0.5 rounded border ${
                                isIgnored ? "border-red-300 bg-red-50 text-red-700"
                                : overrideVal ? "border-blue-300 bg-blue-50 text-blue-900"
                                : "border-slate-200 bg-white text-slate-700"
                              } max-w-full`}
                              style={{ maxWidth: "260px" }}
                            >
                              <option value="">
                                ◀ Auto: {f.matched_column || "(lipsă)"}
                              </option>
                              <option value="__IGNORE__">⊘ Ignoră acest câmp</option>
                              <optgroup label="Coloane din fișier">
                                {uniqueCols.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </optgroup>
                            </select>
                          </td>
                          <td className="text-right px-3 py-1.5 font-mono">
                            {effectiveCol && !isIgnored ? `${f.empty_pct}%` : "—"}
                          </td>
                          <td className="text-right px-3 py-1.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${sevColor}`}>
                              {isIgnored ? "ignored" : f.severity}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Iter51 E33 Faza 3 — profile selector + save button */}
              <div className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="preflight-profile-bar">
                <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                  Profil de mapping (salvat la nivel de sistem)
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={selectedProfileId[preflightResult._source] || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedProfileId((prev) => ({ ...prev, [preflightResult._source]: id || undefined }));
                      // Auto-apply profile mappings into override state
                      if (id) {
                        const prof = mappingProfiles.find((p) => p.id === id);
                        if (prof) setColumnOverrides({ ...(prof.mappings || {}) });
                      } else {
                        setColumnOverrides({});
                      }
                    }}
                    data-testid="preflight-profile-select"
                    className="text-xs border border-slate-300 rounded px-2 py-1"
                  >
                    <option value="">(niciun profil aplicat)</option>
                    {mappingProfiles
                      .filter((p) => p.source === preflightResult._source)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.is_default ? "★ " : ""}{p.name}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveProfile}
                    disabled={savingProfile || Object.keys(columnOverrides).length === 0}
                    data-testid="preflight-save-profile-btn"
                  >
                    {savingProfile ? "Salvează..." : "Salvează ca profil nou"}
                  </Button>
                  {Object.keys(columnOverrides).length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setColumnOverrides({}); setSelectedProfileId((prev) => ({ ...prev, [preflightResult._source]: undefined })); }}
                      data-testid="preflight-clear-overrides-btn"
                    >
                      Resetează overrides
                    </Button>
                  )}
                  <span className="text-[11px] text-slate-600 ml-auto">
                    {Object.keys(columnOverrides).length} override{Object.keys(columnOverrides).length !== 1 ? "-uri" : ""} active
                  </span>
                </div>
              </div>

              {/* Unknown headers */}
              {preflightResult.unknown_headers_count > 0 && (
                <details className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="preflight-unknown" open>
                  <summary className="cursor-pointer font-semibold text-sm text-slate-900">
                    Coloane necunoscute ({preflightResult.unknown_headers_count}) — alege un câmp CRM ca să le incluzi în import
                  </summary>
                  <p className="mt-2 text-xs text-slate-600">
                    Lasă pe "— nu importa —" pentru a păstra comportamentul curent (coloana e ignorată).
                    Mapările salvate aici pot fi salvate ca profil pentru viitor.
                  </p>
                  <div className="mt-3 space-y-2">
                    {preflightResult.unknown_headers.map((h, i) => {
                      // Find any label this column was already mapped to via overrides
                      const mappedToLabel = Object.entries(columnOverrides).find(
                        ([_lbl, col]) => col === h
                      )?.[0] || "";
                      const fieldLabels = (preflightResult.field_quality || []).map(x => ({
                        label: x.label,
                        critical: x.critical,
                      }));
                      // Compute which labels are already taken by ANOTHER header
                      const takenByOthers = new Set(
                        Object.entries(columnOverrides)
                          .filter(([_lbl, col]) => col && col !== h)
                          .map(([lbl, _col]) => lbl)
                      );
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-700 truncate min-w-[180px]" title={h}>· {h}</span>
                          <span className="text-slate-400">→</span>
                          <Select
                            value={mappedToLabel || "__NONE__"}
                            onValueChange={(val) => {
                              setColumnOverrides((prev) => {
                                const next = { ...prev };
                                // Remove any existing override that pointed to this header
                                for (const [lbl, col] of Object.entries(next)) {
                                  if (col === h) delete next[lbl];
                                }
                                if (val !== "__NONE__") next[val] = h;
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger
                              className="h-7 text-xs flex-1 max-w-[320px]"
                              data-testid={`unknown-header-map-${i}`}
                            >
                              <SelectValue placeholder="— nu importa —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__NONE__">— nu importa —</SelectItem>
                              {fieldLabels.map(({ label, critical }) => (
                                <SelectItem
                                  key={label}
                                  value={label}
                                  disabled={takenByOthers.has(label)}
                                >
                                  <span className="inline-flex items-center gap-2">
                                    {critical === "critical" && <span className="text-red-600">●</span>}
                                    {critical === "important" && <span className="text-amber-600">●</span>}
                                    {critical === "optional" && <span className="text-slate-400">●</span>}
                                    {label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreflightResult(null)} data-testid="preflight-close-btn">
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter51 E26 — Cancel Import confirm dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-import-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-500" />
              Oprește importul?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Importul <span className="font-mono text-xs">{cancelTarget?.filename}</span> va fi
              oprit la următorul checkpoint (~50 rânduri). Rândurile deja procesate vor fi
              <strong> păstrate în baza de date</strong> (commit-uri parțiale). Restul fișierului
              nu va fi importat — va trebui să re-uploadezi un fișier nou pentru a continua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelInFlight} data-testid="cancel-confirm-back">
              Înapoi
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelImport}
              disabled={cancelInFlight}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancel-confirm-stop"
            >
              {cancelInFlight ? "Se trimite cererea..." : "Da, oprește importul"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter52 P9 — Click-to-map modal (admin maps unknown category → kind) */}
      <Dialog open={!!mapModal} onOpenChange={(o) => !o && setMapModal(null)}>
        <DialogContent className="max-w-md" data-testid="map-rule-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              Mapează „{mapModal?.value}" la o categorie
            </DialogTitle>
            <DialogDescription>
              Regula se salvează în <strong>Reguli clasificare produse</strong> și se aplică imediat la toate importurile viitoare. Niciun cod nu trebuie modificat.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Sursă import</Label>
              <p className="text-sm font-mono mt-0.5">{mapModal?.source}</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Tip clasificare</Label>
              <Select value={mapKind} onValueChange={setMapKind}>
                <SelectTrigger className="mt-1" data-testid="map-rule-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Iter52 P13g — aligned with Products & Offers page (Anulat removed — it's a status) */}
                  <SelectItem value="EE">EE — Energie electrică</SelectItem>
                  <SelectItem value="Gaz">Gaz</SelectItem>
                  <SelectItem value="VAS">VAS — Valoare Adăugată Servicii</SelectItem>
                  <SelectItem value="HVAS">HVAS — Hard VAS (echipamente)</SelectItem>
                  <SelectItem value="Combo EE + VAS">Combo EE + VAS — Pachet Energie + Servicii</SelectItem>
                  <SelectItem value="Combo Gaz + VAS">Combo Gaz + VAS — Pachet Gaz + Servicii</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Mod de potrivire</Label>
              <Select value={mapMatchType} onValueChange={setMapMatchType}>
                <SelectTrigger className="mt-1" data-testid="map-rule-match-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact (valoarea trebuie să fie identică)</SelectItem>
                  <SelectItem value="contains">Conține (valoarea include un substring)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                {mapMatchType === "exact"
                  ? `Va potrivi DOAR valori egale cu „${mapModal?.value}" (case-insensitive).`
                  : `Va potrivi orice valoare care conține „${mapModal?.value}" — util pentru familii de produse.`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapModal(null)} data-testid="map-rule-cancel">
              Anulează
            </Button>
            <Button onClick={handleSaveRule} disabled={savingRule} data-testid="map-rule-save">
              {savingRule ? "Se salvează..." : "Salvează regula"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter52 P10 — Modificări aplicate de un ImportBatch specific */}
      <Dialog open={!!batchChangesTarget} onOpenChange={(o) => !o && setBatchChangesTarget(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="batch-changes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-600" />
              Modificări aplicate de acest import
            </DialogTitle>
            <DialogDescription>
              {batchChangesTarget?.filename || "—"} · {batchChangesTarget?.source || "—"} ·{" "}
              {batchChangesTarget?.imported_at
                ? new Date(batchChangesTarget.imported_at).toLocaleString("ro-RO")
                : "—"}
            </DialogDescription>
          </DialogHeader>

          {batchChangesLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : batchChangesData ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-3">
              {/* Iter55 — Rezultat import (context complet: creările nu apar în jurnalul de modificări) */}
              {batchChangesData.batch_summary && (
                <div className="rounded-md border border-slate-200 p-2.5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">Rezultat import</p>
                    {(() => {
                      const s = batchChangesData.batch_summary.status;
                      const map = {
                        completed: ["Finalizat", "bg-emerald-100 text-emerald-700 border-emerald-200"],
                        partial: ["Parțial", "bg-amber-100 text-amber-700 border-amber-200"],
                        failed: ["Eșuat", "bg-red-100 text-red-700 border-red-200"],
                        cancelled: ["Oprit", "bg-slate-200 text-slate-700 border-slate-300"],
                        interrupted: ["Întrerupt", "bg-orange-100 text-orange-700 border-orange-200"],
                        processing: ["În progres", "bg-blue-100 text-blue-700 border-blue-200"],
                      };
                      const [label, cls] = map[s] || [s || "—", "bg-slate-100 text-slate-700 border-slate-200"];
                      return <Badge className={cls} data-testid="bc-batch-status">{label}</Badge>;
                    })()}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-sm">
                    <div className="rounded-md border border-slate-200 bg-white p-2 text-center">
                      <p className="text-[11px] text-slate-500">Total rânduri</p>
                      <p className="text-lg font-bold text-slate-800 font-mono">{batchChangesData.batch_summary.rows_total}</p>
                    </div>
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-center">
                      <p className="text-[11px] text-emerald-700">Înregistrări noi create</p>
                      <p className="text-lg font-bold text-emerald-700 font-mono" data-testid="bc-created">{batchChangesData.batch_summary.rows_created}</p>
                    </div>
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-center">
                      <p className="text-[11px] text-blue-700">Actualizate</p>
                      <p className="text-lg font-bold text-blue-700 font-mono" data-testid="bc-updated">{batchChangesData.batch_summary.rows_updated}</p>
                    </div>
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-center">
                      <p className="text-[11px] text-amber-700">Ignorate</p>
                      <p className="text-lg font-bold text-amber-700 font-mono" data-testid="bc-skipped">{batchChangesData.batch_summary.rows_skipped}</p>
                    </div>
                    <div className={`rounded-md border p-2 text-center ${batchChangesData.batch_summary.rows_errors > 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                      <p className={`text-[11px] ${batchChangesData.batch_summary.rows_errors > 0 ? "text-red-700" : "text-slate-500"}`}>Erori</p>
                      <p className={`text-lg font-bold font-mono ${batchChangesData.batch_summary.rows_errors > 0 ? "text-red-700" : "text-slate-800"}`} data-testid="bc-errors">{batchChangesData.batch_summary.rows_errors}</p>
                    </div>
                  </div>
                  {batchChangesData.batch_summary.duration_seconds > 0 && (
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Durată: {Math.round(batchChangesData.batch_summary.duration_seconds)}s
                      {batchChangesData.batch_summary.rows_total > 0 && (
                        <> · {(batchChangesData.batch_summary.rows_total / batchChangesData.batch_summary.duration_seconds).toFixed(1)} rânduri/s</>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Motive ignorare / eroare */}
              {(batchChangesData.skipped_reasons?.length > 0 || batchChangesData.error_reasons?.length > 0) && (
                <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2">
                  <p className="text-xs font-semibold text-amber-800 mb-1.5">De ce nu au intrat toate rândurile</p>
                  <div className="flex flex-wrap gap-1.5">
                    {batchChangesData.skipped_reasons?.map((r, i) => (
                      <span key={`s${i}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-[11px]" data-testid={`bc-skip-reason-${i}`}>
                        <span className="text-amber-800">Ignorat:</span> {r.reason}
                        <span className="font-bold text-amber-900">×{r.count}</span>
                      </span>
                    ))}
                    {batchChangesData.error_reasons?.map((r, i) => (
                      <span key={`e${i}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 border border-red-200 text-[11px]" data-testid={`bc-error-reason-${i}`}>
                        <span className="text-red-800">Eroare:</span> {r.reason}
                        <span className="font-bold text-red-900">×{r.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Modificări pe date existente (din jurnalul de câmpuri) */}
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5">
                  Modificări pe înregistrări existente
                  <span className="font-normal text-slate-400"> — crearea de înregistrări noi nu apare aici (vezi „create” mai sus)</span>
                </p>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-center">
                    <p className="text-xs text-emerald-700">Câmpuri modificate</p>
                    <p className="text-xl font-bold text-emerald-700 font-mono" data-testid="bc-total-field-changes">
                      {batchChangesData.total_field_changes}
                    </p>
                  </div>
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-center">
                    <p className="text-xs text-blue-700">Tranziții status</p>
                    <p className="text-xl font-bold text-blue-700 font-mono" data-testid="bc-total-status-changes">
                      {batchChangesData.total_status_changes}
                    </p>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-2 text-center">
                    <p className="text-xs text-slate-600">Câmpuri pe contracte</p>
                    <p className="text-xl font-bold text-slate-800 font-mono">
                      {batchChangesData.counts_by_entity?.contract || 0}
                    </p>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-2 text-center">
                    <p className="text-xs text-slate-600">Câmpuri pe clienți</p>
                    <p className="text-xl font-bold text-slate-800 font-mono">
                      {batchChangesData.counts_by_entity?.client || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Top fields */}
              {batchChangesData.top_fields?.length > 0 && (
                <div className="rounded-md border border-slate-200 p-2">
                  <p className="text-xs font-semibold text-slate-700 mb-1.5">
                    Top câmpuri modificate
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {batchChangesData.top_fields.map((f, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-mono"
                      >
                        {f.field}
                        <span className="font-bold text-slate-700">×{f.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Events list */}
              <div className="flex-1 overflow-y-auto rounded-md border border-slate-200">
                {batchChangesData.events?.length === 0 ? (
                  <p className="p-6 text-center text-sm text-slate-500 italic">
                    Acest import nu a produs modificări de câmpuri detectabile.
                    <br />
                    (Posibil: doar inserții noi, fără update-uri pe entități existente.)
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[11px] text-slate-600 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">Când</th>
                        <th className="text-left px-2 py-1.5">Entitate</th>
                        <th className="text-left px-2 py-1.5">Câmp</th>
                        <th className="text-left px-2 py-1.5">Vechi</th>
                        <th className="text-left px-2 py-1.5">Nou</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchChangesData.events.map((ev, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`bc-event-${i}`}>
                          <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                            {ev.when ? new Date(ev.when).toLocaleTimeString("ro-RO") : "—"}
                          </td>
                          <td className="px-2 py-1.5">
                            <Badge variant="outline" className="text-[10px]">
                              {ev.entity_type}
                            </Badge>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px] font-semibold">
                            {ev.field}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 font-mono text-[11px] max-w-[160px] truncate">
                            {ev.old || <span className="italic text-slate-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-emerald-700 font-mono text-[11px] max-w-[160px] truncate">
                            {ev.new || <span className="italic text-slate-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <p className="p-6 text-center text-sm text-slate-500">Niciun date</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchChangesTarget(null)}>
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportPage;
